import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { isCancelledError } from '@client/components/_ui/feedback/ErrorNotification';
import type { message } from '@client/components/_ui/feedback/message';
import type { modal } from '@client/components/_ui/feedback/modal';
import { useAuthStore } from '@client/stores/AuthStore';
import { isNetworkError } from '@client/utils/errors/network-error-detection';
import { LITE_UPGRADE_ERROR_PREFIX } from '@shared/constants/business/lite';
import { hasPermission } from '@shared/permissions/roles';
import { formatErrorMessage } from './ErrorFormatters';
import { getErrorModalAction } from './errorModalAction';
import { extractErrorTitle } from './errorTitleExtraction';
import type { QueuedError, ShowAsyncOptions, ShowErrorOptions } from './types';

/** True for server rejections thrown by the Curium Lite plan gate (`upgrade_required: …`). */
function isLiteUpgradeRequiredError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string' &&
        (error as { message: string }).message.startsWith(LITE_UPGRADE_ERROR_PREFIX)
    );
}

/**
 * A modal currently on screen, with the answer it will settle to. `actionable` is true only
 * for `showAsync` modals, which render the caller's OK button and can resolve `'ok'`.
 */
interface OpenModal {
    answer: Promise<'ok' | 'cancel'>;
    actionable: boolean;
}

class ErrorMessage {
    modalApi?: typeof modal;
    messageApi?: typeof message;
    private errorQueue: QueuedError[] = [];
    private debounceTimer?: NodeJS.Timeout;
    private readonly DEBOUNCE_MS = 300; // 300ms debounce window
    private readonly NETWORK_ERROR_DEBOUNCE_MS = 1000; // 1s debounce for network errors
    private activeNetworkErrorModal: boolean = false; // Track if network error modal is open
    /**
     * Modals currently open, keyed by error signature. Callers such as a React Query
     * `throwOnError` predicate run on every render while the query stays errored, so
     * without this guard the same modal is opened again and again and its masks stack
     * into a near-black backdrop. An identical error reuses the open modal instead;
     * for `showAsync` the caller shares that modal's promise — but only when the open
     * modal can actually answer `'ok'` (`actionable`), since a `show()` modal offers no
     * OK affordance and always settles `'cancel'`.
     *
     * A signature can hold more than one entry: a non-actionable `show()` modal may still be
     * on screen when `showAsync` opens its own actionable one, and closing either must leave
     * the other registered so the remaining modal keeps suppressing duplicates.
     */
    private openModals = new Map<string, OpenModal[]>();

    init(modalApi: typeof modal, messageApi: typeof message) {
        this.modalApi = modalApi;
        this.messageApi = messageApi;
    }

    /**
     * True once `init()` has wired up the Ant Design modal/message APIs.
     * The global `QueryCache.onError` handler uses this to suppress modals
     * for failures that land before `AppProvider` mounts (preloads, loaders).
     */
    get isInitialised(): boolean {
        return this.modalApi !== undefined && this.messageApi !== undefined;
    }

    /**
     * Calculate optimal modal width based on error message content
     * @param error - The error(s) to analyze
     * @param minWidth - Minimum width to enforce
     * @returns Calculated width in pixels
     */
    private calculateModalWidth(error: unknown | unknown[], minWidth: number = 520): number {
        const maxWidth = 800; // Maximum width to avoid overly wide modals
        const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const maxAllowedWidth = Math.min(maxWidth, screenWidth * 0.9); // 90% of screen width max

        try {
            let content = '';

            if (Array.isArray(error)) {
                // For multiple errors, estimate total content length
                content = error
                    .map((e) => {
                        if (typeof e === 'string') return e;
                        if (e && typeof e === 'object' && 'message' in e) return String(e.message);
                        return JSON.stringify(e);
                    })
                    .join(' ');
            } else if (typeof error === 'string') {
                content = error;
            } else if (error && typeof error === 'object' && 'message' in error) {
                content = String(error.message);
            } else {
                content = JSON.stringify(error);
            }

            // Estimate width based on content length
            const baseCharWidth = 8; // Approximate character width in pixels
            const padding = 80; // Modal padding and margins
            const estimatedWidth = content.length * baseCharWidth + padding;

            // Consider line breaks - longer content might wrap
            const lineCount = Math.max(1, Math.ceil(content.length / 70)); // ~70 chars per line
            const adjustedWidth = lineCount > 1 ? estimatedWidth * 0.7 : estimatedWidth;

            // Apply constraints
            return Math.max(minWidth, Math.min(adjustedWidth, maxAllowedWidth));
        } catch {
            // Fallback to minimum width if calculation fails
            return minWidth;
        }
    }

    private getErrorSignature(error: unknown): string {
        // Network errors get a common signature for proper deduplication
        if (isNetworkError(error)) {
            return 'NETWORK_ERROR';
        }

        if (typeof error === 'object' && error !== null) {
            const message = (error as any).message;
            const code = (error as any).data?.code;
            if (message && code) {
                return `${message}::${code}`;
            }
            if (message) {
                return message;
            }
        }
        try {
            return JSON.stringify(error);
        } catch {
            return 'un-serializable-error';
        }
    }

    /**
     * Drop only the closing modal's own registry entry. `settle` runs on OK/cancel and again
     * from `afterClose` after the close animation; an identical modal opened in that window
     * must not be unregistered by the old one's late callback, and any sibling modal for the
     * same signature that is still on screen stays registered.
     */
    private releaseModal(signature: string, modal: Promise<'ok' | 'cancel'>) {
        const entries = this.openModals.get(signature);
        if (!entries) return;

        const remaining = entries.filter((entry) => entry.answer !== modal);
        if (remaining.length === entries.length) return;

        if (remaining.length === 0) {
            this.openModals.delete(signature);
        } else {
            this.openModals.set(signature, remaining);
        }
    }

    /** Register a freshly opened modal alongside any other modal still open for `signature`. */
    private registerModal(signature: string, entry: OpenModal) {
        this.openModals.set(signature, [...(this.openModals.get(signature) ?? []), entry]);
    }

    private getModalSignature(error: unknown | unknown[]): string {
        return (Array.isArray(error) ? error : [error]).map((e) => this.getErrorSignature(e)).join('||');
    }

    private processErrorQueue() {
        if (this.errorQueue.length === 0) return;

        if (!this.modalApi || !this.messageApi) {
            throw new Error('Error modal not initialized.');
        }

        this.messageApi.destroy();

        const allQueuedErrors = this.errorQueue.flatMap((item) =>
            Array.isArray(item.error)
                ? item.error.map((e) => ({ error: e, title: item.title }))
                : [{ error: item.error, title: item.title }],
        );

        if (allQueuedErrors.length === 0) {
            this.errorQueue = [];
            return;
        }

        const uniqueErrors = new Map<string, { error: unknown; title?: string }>();
        for (const item of allQueuedErrors) {
            const signature = this.getErrorSignature(item.error);
            if (!uniqueErrors.has(signature)) {
                uniqueErrors.set(signature, item);
            }
        }

        const deduplicatedItems = Array.from(uniqueErrors.values());

        if (deduplicatedItems.length === 1) {
            // Single error - show normally
            const { error, title } = deduplicatedItems[0];
            this.showSingle(error, title);
        } else {
            // Multiple errors - combine them
            const errors = deduplicatedItems.map((item) => item.error);
            const titles = deduplicatedItems.map((item) => item.title || extractErrorTitle(item.error)).filter(Boolean);

            // Use a generic title for multiple errors, or the most specific one
            const combinedTitle =
                titles.length > 0 && titles.every((t) => t === titles[0])
                    ? titles[0]
                    : `Multiple Errors (${errors.length})`;

            this.showSingle(errors, combinedTitle);
        }

        this.errorQueue = [];
    }

    private showSingle(error: unknown | unknown[], title?: string) {
        if (!this.modalApi) return;

        // Check if this is a network error FIRST
        const errors = Array.isArray(error) ? error : [error];
        const hasNetworkError = errors.some(isNetworkError);

        // If a network error modal is already active, don't show another one
        if (hasNetworkError && this.activeNetworkErrorModal) {
            return;
        }

        // Bail out before mutating any state: the flag must stay owned by the modal that
        // set it, and this call is about to open nothing.
        const signature = this.getModalSignature(error);
        if (this.openModals.has(signature)) {
            return;
        }

        // Set tracking flag IMMEDIATELY for network errors to prevent race conditions
        if (hasNetworkError) {
            this.activeNetworkErrorModal = true;
        }

        let settleOpen: (outcome: 'ok' | 'cancel') => void = () => {};
        const open = new Promise<'ok' | 'cancel'>((resolve) => {
            settleOpen = resolve;
        });

        const extractedTitle = extractErrorTitle(error);
        const finalTitle = title ?? extractedTitle;
        const width = this.calculateModalWidth(error, 520);
        const { user, org } = useAuthStore.getState();
        const action =
            typeof window !== 'undefined'
                ? getErrorModalAction({
                      error,
                      baseUrl: window.location.origin,
                      canOpenSettings: hasPermission(user?.roles ?? [], 'settings:update'),
                      orgId: org?.id,
                  })
                : null;

        const modal = this.modalApi.error({
            title: finalTitle,
            content: <div className="py-4 pr-2">{formatErrorMessage(error)}</div>,
            width,
            centered: true,
            footer: () => (
                <div className="flex justify-end gap-2">
                    {action && (
                        <Button
                            type="primary"
                            onClick={() => {
                                modal.destroy();
                                window.location.href = action.href;
                            }}
                        >
                            {action.label}
                        </Button>
                    )}
                    <Button type="secondary" onClick={() => modal.destroy()}>
                        Close
                    </Button>
                </div>
            ),
            afterClose: () => {
                this.releaseModal(signature, open);
                settleOpen('cancel');
                // Clear tracking flag when modal closes
                if (hasNetworkError) {
                    this.activeNetworkErrorModal = false;
                }
            },
        });
        // Registered only once the modal is actually open, so a throw above cannot leave a
        // stale entry that would suppress this error for the rest of the session.
        // Not actionable: this footer only offers "Close", so it can never answer 'ok'.
        this.registerModal(signature, { answer: open, actionable: false });

        return modal;
    }

    /**
     * Show an error modal with optional debouncing to batch multiple errors
     * @param error - The error(s) to display
     * @param title - Optional custom title for the error modal
     * @param options - Configuration options
     * @param options.immediate - If true, show the error immediately without debouncing (default: false)
     *
     * @example
     * // Normal usage (with 300ms debouncing)
     * errorMessage.show(error);
     *
     * // Show immediately without batching
     * errorMessage.show(error, 'Critical Error', { immediate: true });
     *
     * // Multiple errors within 300ms will be automatically batched
     * errorMessage.show(error1);
     * errorMessage.show(error2); // Will be combined with error1
     */
    show(error: unknown | unknown[], title?: string, options?: ShowErrorOptions) {
        if (!this.modalApi || !this.messageApi) {
            throw new Error('Error modal not initialized.');
        }

        // Silently ignore cancelled errors (e.g., from navigation during polling)
        const errorsToCheck = Array.isArray(error) ? error : [error];
        if (errorsToCheck.every(isCancelledError)) {
            return;
        }

        // Curium Lite (CUR-379): `upgrade_required`-prefixed FORBIDDEN errors are expected
        // plan-gate rejections, not faults — surface a friendly upgrade prompt instead of an
        // error modal. Backstop for any surface missing a client-side lock.
        if (errorsToCheck.length > 0 && errorsToCheck.every(isLiteUpgradeRequiredError)) {
            void this.messageApi.warning(
                'This feature is available on the full Curium plan. Talk to us about upgrading.',
            );
            return;
        }

        // Check for network errors FIRST
        const errors = Array.isArray(error) ? error : [error];
        const hasNetworkError = errors.some(isNetworkError);

        // If a network error modal is already active, don't queue another one
        if (hasNetworkError && this.activeNetworkErrorModal) {
            return;
        }

        // NOTE: Don't set activeNetworkErrorModal flag here - it should only be set
        // in showSingle() when the modal is actually created. Setting it here would
        // cause showSingle() to return early when called from processErrorQueue().

        // If immediate option is true, bypass debouncing
        if (options?.immediate) {
            this.messageApi.destroy();
            return this.showSingle(error, title);
        }

        // Add to queue
        this.errorQueue.push({ error, title, timestamp: Date.now() });

        // Clear existing timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // Set new timer with appropriate debounce time
        const debounceTime =
            options?.debounceMs ?? (hasNetworkError ? this.NETWORK_ERROR_DEBOUNCE_MS : this.DEBOUNCE_MS);
        this.debounceTimer = setTimeout(() => {
            this.processErrorQueue();
        }, debounceTime);
    }

    /**
     * Clear any pending errors in the queue
     */
    clearPendingErrors() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = undefined;
        }
        this.errorQueue = [];
    }

    /**
     * Get the number of pending errors in the queue
     */
    getPendingErrorCount(): number {
        return this.errorQueue.length;
    }

    validation(error: unknown | unknown[], title: string = 'Validation Error') {
        if (!this.modalApi || !this.messageApi) {
            throw new Error('Error modal not initialized.');
        }

        // Validation errors are shown immediately to avoid user confusion
        this.messageApi.destroy();

        const extractedTitle = extractErrorTitle(error);
        const finalTitle = extractedTitle !== 'An Error Occurred' ? extractedTitle : title;
        const width = this.calculateModalWidth(error, 480);

        return this.modalApi.error({
            title: finalTitle,
            content: <div className="py-4">{formatErrorMessage(error)}</div>,
            okText: 'Got it',
            okType: 'primary',
            mask: { closable: true },
            centered: true,
            width,
        });
    }

    showAsync(error: unknown | unknown[], opts: ShowAsyncOptions = { okText: 'Ok' }): Promise<'ok' | 'cancel'> {
        const modalApi = this.modalApi;
        const messageApi = this.messageApi;
        if (!modalApi || !messageApi) {
            throw new Error('Error modal not initialized.');
        }

        // Silently ignore cancelled errors (e.g., from navigation during polling)
        const errorsToCheck = Array.isArray(error) ? error : [error];
        if (errorsToCheck.every(isCancelledError)) {
            return Promise.resolve('cancel');
        }

        // Curium Lite (CUR-379): plan-gate rejections get the friendly upgrade prompt here
        // too, not a raw `upgrade_required: …` error modal (mirrors show()).
        if (errorsToCheck.length > 0 && errorsToCheck.every(isLiteUpgradeRequiredError)) {
            void messageApi.warning('This feature is available on the full Curium plan. Talk to us about upgrading.');
            return Promise.resolve('ok');
        }

        // Check if this is a network error
        const hasNetworkError = errorsToCheck.some(isNetworkError);

        // If a network error modal is already active, don't show another one
        if (hasNetworkError && this.activeNetworkErrorModal) {
            return Promise.resolve('cancel');
        }

        // The same error is already on screen: share its modal (and its answer) instead of
        // stacking another one over it. A `show()` modal only offers "Close" and always
        // settles 'cancel', so it cannot stand in for this caller's action button — open a
        // fresh, actionable modal in that case.
        const signature = this.getModalSignature(error);
        const open = this.openModals.get(signature)?.find((entry) => entry.actionable);
        if (open) {
            return open.answer;
        }

        // Set tracking flag IMMEDIATELY for network errors to prevent race conditions
        // This must happen before any other operations (messageApi.destroy, calculateModalWidth, etc.)
        if (hasNetworkError) {
            this.activeNetworkErrorModal = true;
        }

        messageApi.destroy();

        const width = this.calculateModalWidth(error, 480);

        const pending = new Promise<'ok' | 'cancel'>((resolve) => {
            const settle = (outcome: 'ok' | 'cancel') => {
                this.releaseModal(signature, pending);
                // Clear tracking flag when modal closes
                if (hasNetworkError) {
                    this.activeNetworkErrorModal = false;
                }
                resolve(outcome);
            };

            modalApi.error({
                title: opts.title || extractErrorTitle(error),
                content: <div className="py-4">{formatErrorMessage(error)}</div>,
                okText: opts.okText || 'Ok',
                width,
                centered: true,
                onCancel: () => settle('cancel'),
                onOk: () => settle('ok'),
                afterClose: () => settle('cancel'),
            });
        });

        this.registerModal(signature, { answer: pending, actionable: true });
        return pending;
    }
}

const errorMessage = new ErrorMessage();

export { errorMessage };
